"use client"

/**
 * RecommendationWidgetAd — ExoClick's Native "Recommendation Widget": a
 * "you may also like" strip of image+title tiles, styled to look like
 * content rather than a banner. Rendered once, below the main grid (see
 * booru-browser.tsx) — not threaded into booru-grid.tsx's virtualized,
 * per-post ad-injection system, since this only ever appears once per
 * page/session, not on a repeating cadence.
 *
 * The zone itself lays out its own grid (4 cols x 1 row desktop, 1 col x 2
 * rows mobile, configured in the ExoClick dashboard) — this just gives it a
 * responsive-width, reasonable-height box to render into, same "no single
 * native size, let the network's own script fill the box" reasoning as
 * VideoSliderAd/InPagePushAd/InstantMessageAd.
 */

import { useEffect, useState } from "react"
import { useAdStore } from "@/lib/ad-store"
import { useHasConsent } from "@/components/cookie-consent"
import { useAdTier } from "@/lib/ad-tier"
import { useAdFrequencyStore } from "@/lib/ad-frequency-store"
import { isAdCooldownExpired, isAdTestModeOn, markAdShown } from "@/lib/ad-cooldown"
import { isGlobalAdsOff } from "@/lib/global-ads-off"
import { extractExoclickZoneId, exoclickIframeSrc } from "@/lib/ad-embed"
import { useTranslations } from "next-intl"

interface AdData {
  zone_id: string
  zone_type: string
  embed_code: string | null
}

export function RecommendationWidgetAd() {
  const t = useTranslations("recommendationWidgetAd")
  const hasConsent = useHasConsent()
  const { isPremiumUser } = useAdStore()
  // Force Ads test mode escape hatch — see the matching comment on
  // NetworkDisplayAd.tsx. Admin/super_admin accounts always read
  // isPremiumUser: true, so without this, Force Ads could never actually
  // force this widget to show for its only realistic tester.
  const effectivePremiumUser = (isPremiumUser || isGlobalAdsOff()) && !isAdTestModeOn()
  const adTier = useAdTier()
  const [adData, setAdData] = useState<AdData | null>(null)

  useEffect(() => {
    if (!hasConsent || effectivePremiumUser) return
    if (!useAdFrequencyStore.getState().isEnabled("recommendation_widget_ads")) return
    const cooldown = useAdFrequencyStore.getState().getCooldownMinutes("recommendation_widget_ads")
    if (!isAdCooldownExpired("recommendation_widget", cooldown)) return

    fetch(`/api/ads/network-grid-ad?type=recommendation_widget&tier=${adTier}${isAdTestModeOn() ? "&preview=1" : ""}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.zone_id) {
          setAdData(d)
          markAdShown("recommendation_widget")
        }
      })
      .catch(() => {})
  }, [hasConsent, effectivePremiumUser, adTier])

  if (!hasConsent || effectivePremiumUser || !adData) return null

  const isEmbed = adData.zone_type === "js_embed"
  const zoneId = isEmbed ? extractExoclickZoneId(adData.embed_code) : null
  // A recognized ExoClick tag gets a real cross-origin iframe (its own
  // origin, so its cookie writes succeed). Anything else — e.g. a
  // ClickAdilla `<script data-admpid>` + `<div data-banner-id>` pair, which
  // has no equivalent per-zone URL to point a real src= at — runs via
  // srcDoc in an opaque-origin sandboxed frame instead, same pattern
  // already proven safe on StickyBannerAd/PillarboxAd/LetterboxAd: the
  // script only needs its own target div in the SAME document it executes
  // in, which this iframe provides without ever running unsandboxed in our
  // real DOM. Previously this component only handled the ExoClick case and
  // silently rendered nothing for any other network's zone.
  if (!isEmbed || !adData.embed_code) return null

  return (
    <div className="w-full h-[260px] my-2 overflow-hidden rounded-lg">
      {zoneId ? (
        <iframe
          src={exoclickIframeSrc(zoneId)}
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          title={t("advertisement")}
          scrolling="no"
          style={{ width: "100%", height: "100%", border: 0, display: "block", background: "transparent" }}
        />
      ) : (
        <iframe
          srcDoc={`<!DOCTYPE html><html><head><style>html,body{margin:0;padding:0;overflow:hidden;background:transparent}</style></head><body>${adData.embed_code}</body></html>`}
          sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
          title={t("advertisement")}
          scrolling="no"
          style={{ width: "100%", height: "100%", border: 0, display: "block", background: "transparent" }}
        />
      )}
    </div>
  )
}
